/* jp-privacy.js — one consent boundary for optional storefront state and analytics.
   Shopify's Customer Privacy API remains the authority: controllers may keep an
   explicit interaction in page memory, but local/session storage is read or written
   only when preferencesProcessingAllowed() is true. Analytics is default-denied
   until analyticsProcessingAllowed() is true. Revocation purges only jp-owned keys. */
(() => {
  if (window.__jpPrivacyInit) return;
  window.__jpPrivacyInit = true;

  window.JapanScissors = window.JapanScissors || {};

  const localMemory = new Map();
  const sessionMemory = new Map();
  const LOCAL_KEYS = [
    'jp_persona_state',
    'jp_collection_view',
    'jp_col_views',
    'jp_nudge_dismissed',
    'jp_recent_searches',
    'jp_recent_products',
    'jp_article_signup_dismissed',
    'jp_shortlist',
  ];
  const SESSION_KEYS = ['jp_persona_url_ack'];
  const LOCAL_PREFIXES = ['jp_steer_dismissed_'];
  let loading = false;
  let state = {
    ready: false,
    preferences: false,
    analytics: false,
    marketing: false,
    saleOfData: false,
  };

  const safeAllowed = (api, method) => {
    try { return typeof api[method] === 'function' && api[method]() === true; }
    catch (e) { return false; }
  };
  const remove = (storageName, key) => {
    try { window[storageName].removeItem(key); } catch (e) { /* unavailable/private storage */ }
  };
  const purgeOwnedStorage = () => {
    LOCAL_KEYS.forEach((key) => remove('localStorage', key));
    SESSION_KEYS.forEach((key) => remove('sessionStorage', key));
    try {
      const storage = window.localStorage;
      for (let i = storage.length - 1; i >= 0; i -= 1) {
        const key = storage.key(i);
        if (key && LOCAL_PREFIXES.some((prefix) => key.indexOf(prefix) === 0)) remove('localStorage', key);
      }
    } catch (e) { /* unavailable/private storage */ }
    localMemory.clear();
    sessionMemory.clear();
  };
  const publish = (previous) => {
    document.dispatchEvent(new CustomEvent('jp:privacy:change', {
      detail: Object.assign({ previous }, state),
    }));
  };
  const deny = () => {
    const previous = Object.assign({}, state);
    state = { ready: true, preferences: false, analytics: false, marketing: false, saleOfData: false };
    purgeOwnedStorage();
    publish(previous);
  };
  const refresh = () => {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) { deny(); return; }
    const previous = Object.assign({}, state);
    state = {
      ready: true,
      preferences: safeAllowed(api, 'preferencesProcessingAllowed'),
      analytics: safeAllowed(api, 'analyticsProcessingAllowed'),
      marketing: safeAllowed(api, 'marketingAllowed'),
      saleOfData: safeAllowed(api, 'saleOfDataAllowed'),
    };
    if (!state.preferences) purgeOwnedStorage();
    publish(previous);
  };

  // Resolve storage inside the guarded operation: the browser property getter
  // itself can throw, before getItem/setItem/removeItem is ever reached.
  const readPreference = (storageName, memory, key) => {
    if (memory.has(key)) return memory.get(key);
    if (!state.preferences) return null;
    try { return window[storageName].getItem(key); } catch (e) { return null; }
  };
  const writePreference = (storageName, memory, key, value) => {
    const normalized = String(value);
    memory.set(key, normalized);
    if (!state.preferences) return;
    try { window[storageName].setItem(key, normalized); } catch (e) { /* unavailable/private storage */ }
  };
  const removePreference = (storageName, memory, key) => {
    memory.delete(key);
    if (state.preferences) remove(storageName, key);
  };

  const privacy = {
    getState: () => Object.assign({}, state),
    preferencesAllowed: () => state.preferences === true,
    analyticsAllowed: () => state.analytics === true,
    getPreference: (key) => readPreference('localStorage', localMemory, key),
    setPreference: (key, value) => writePreference('localStorage', localMemory, key, value),
    removePreference: (key) => removePreference('localStorage', localMemory, key),
    getSessionPreference: (key) => readPreference('sessionStorage', sessionMemory, key),
    setSessionPreference: (key, value) => writePreference('sessionStorage', sessionMemory, key, value),
    removeSessionPreference: (key) => removePreference('sessionStorage', sessionMemory, key),
    refresh,
  };
  window.JapanScissors.privacy = privacy;

  const load = () => {
    if (loading) return;
    if (window.Shopify && window.Shopify.customerPrivacy) { refresh(); return; }
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') { deny(); return; }
    loading = true;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        loading = false;
        if (error) { deny(); return; }
        refresh();
      },
    );
  };

  document.addEventListener('visitorConsentCollected', refresh);
  window.addEventListener('pageshow', () => {
    if (window.Shopify && window.Shopify.customerPrivacy) refresh();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', load, { once: true });
  else load();
})();
